import {messages} from '@smartmixin/i18n';
import type {IntlShape, MessageDescriptor} from 'react-intl';
import type {
  Location,
  PlaceAutocomplete,
  PlaceIdDetail,
  WeatherStationFromList,
} from '../../api/types';

/**
 * My weather's one search (FE #381 step 2): places from Google and stations
 * by name, from one field. What both apps share; the hook that asks is
 * `useMyWeatherSearch`.
 */

/**
 * The shortest query searched. The server refuses shorter autocompletes
 * (`PlaceAutocompleteSerializer`), and a two-letter station name search
 * matches thousands.
 */
export const SEARCH_MIN_LENGTH = 3;

/** Stations listed before "Show all". */
export const STATIONS_SHOWN = 5;

/** The query to search for this text, or null when it is too short. */
export const searchQuery = (text: string): string | null => {
  const query = text.trim();
  return query.length >= SEARCH_MIN_LENGTH ? query : null;
};

type Position = {latitude: number; longitude: number};

/**
 * The stations whose name holds the query. With a position the server sorts
 * them nearest first and gives each its distance: what makes 25 stations
 * all named "Lyon" usable.
 */
export const stationSearchFilter = (
  query: string,
  position: Position | null,
): {name: string; location?: string} =>
  position
    ? {name: query, location: `${position.longitude},${position.latitude}`}
    : {name: query};

/**
 * The stations nearest a position, whatever their name (the search field's
 * ⌖): the server sorts them nearest first and pages them, all of them.
 */
export const stationNearbyFilter = (
  position: Position,
): {location: string} => ({
  location: `${position.longitude},${position.latitude}`,
});

/**
 * The place a picked suggestion saves: what the place picker has always
 * saved, the Google place id as its hash, so devices name it alike.
 */
export const placeFromPick = (
  detail: PlaceIdDetail,
  pick: PlaceAutocomplete,
): Location => ({
  hash: pick.place_id,
  coordinates: {
    latitude: detail.geometry.location.lat,
    longitude: detail.geometry.location.lng,
  },
  title: pick.structured_formatting.main_text,
  subtitle: pick.structured_formatting.secondary_text,
  timezone: detail.timezone,
});

/**
 * What an edit of the label the ⌖ put in the search field ("My location")
 * means. The text alone cannot tell a letter typed over the selected label
 * (Android selects it on focus) from the rest of it deleted, so only one
 * letter less is a deletion. The text to search: what was inserted,
 * wherever the caret was (iOS selects nothing on a tap and places it where
 * tapped), while the whole label is still there around it, or else the
 * value as typed (what replaced the selected label). Null to clear:
 * emptied, or one letter deleted.
 */
export const editOfLabel = (label: string, value: string): string | null => {
  if (value === '') {
    return null;
  }
  let prefix = 0;
  while (
    prefix < Math.min(label.length, value.length) &&
    label[prefix] === value[prefix]
  ) {
    prefix += 1;
  }
  let suffix = 0;
  while (
    suffix < Math.min(label.length, value.length) - prefix &&
    label[label.length - 1 - suffix] === value[value.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  const kept = prefix + suffix;
  if (value.length === label.length - 1 && kept === value.length) {
    return null;
  }
  if (kept === label.length) {
    return value.slice(prefix, value.length - suffix).trimStart();
  }
  return value;
};

/** What each source of the search holds (`useMyWeatherSearch`). */
export type SearchStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Set on a failed source refused for too many requests (a 429, FE #427):
 * its row and the spoken summary say that instead. Read only with the status
 * `error`.
 */
type Throttled = {throttled?: true};

export type PlaceResults = {
  status: SearchStatus;
  items: PlaceAutocomplete[];
} & Throttled;

export type StationResults = Throttled & {
  status: SearchStatus;
  items: WeatherStationFromList[];
  /** How many stations match in all, over every page. */
  count: number;
  hasMore: boolean;
  /** A further page is on its way. */
  loadingMore: boolean;
  /**
   * The last further page failed (#412). No further page is asked for
   * until `retryMoreStations`, which asks that one again.
   */
  moreFailed: boolean;
};

export type SearchSourceName = 'places' | 'stations';

/**
 * One row of the search results, in order: a section's header, then its
 * rows, a message when it found nothing or failed, and "Show all". A list,
 * because which rows there are is the logic; each app draws them.
 */
export type SearchRow =
  | {type: 'header'; key: string; source: SearchSourceName; nearby?: true}
  | {type: 'place'; key: string; place: PlaceAutocomplete}
  | {type: 'station'; key: string; station: WeatherStationFromList}
  | {type: 'empty'; key: string; source: SearchSourceName}
  | ({type: 'error'; key: string; source: SearchSourceName} & Throttled)
  | {type: 'more'; key: string};

/** A failed section's row: refused for too many requests, or failed. */
const errorRow = (
  source: SearchSourceName,
  {throttled}: Throttled,
): SearchRow => ({
  type: 'error',
  key: `${source}-error`,
  source,
  ...(throttled ? {throttled} : {}),
});

/** One section's rows, after its header. */
const sectionRows = (
  source: SearchSourceName,
  results: {status: SearchStatus} & Throttled,
  rows: SearchRow[],
): SearchRow[] => {
  const {status} = results;
  if (status === 'error') {
    return [errorRow(source, results)];
  }
  if (status === 'ready' && rows.length === 0) {
    return [{type: 'empty', key: `${source}-empty`, source}];
  }
  return rows;
};

const stationRows = (items: WeatherStationFromList[]): SearchRow[] =>
  items.map((station) => ({
    type: 'station' as const,
    key: `station-${station.id}`,
    station,
  }));

/**
 * The results (FE #381, design S1): places first, then stations by name,
 * nearest first, `STATIONS_SHOWN` and then "Show all" (`expanded` once
 * pressed). Each section loads and fails on its own. Around the user (the
 * field's ⌖, design R1) the stations alone, nearest first, never empty and
 * never capped: every station there is answers.
 */
export const searchRows = (
  places: PlaceResults,
  stations: StationResults,
  {expanded, nearby}: {expanded: boolean; nearby: boolean},
): SearchRow[] => {
  if (nearby) {
    return [
      {type: 'header', key: 'nearby-header', source: 'stations', nearby: true},
      ...(stations.status === 'error'
        ? [errorRow('stations', stations)]
        : stationRows(stations.items)),
    ];
  }
  const shown = expanded
    ? stations.items
    : stations.items.slice(0, STATIONS_SHOWN);
  const more: SearchRow[] =
    !expanded && stations.status === 'ready' && stations.count > STATIONS_SHOWN
      ? [{type: 'more', key: 'stations-more'}]
      : [];
  return [
    {type: 'header', key: 'places-header', source: 'places'},
    ...sectionRows(
      'places',
      places,
      places.items.map((place) => ({
        type: 'place' as const,
        key: `place-${place.place_id}`,
        place,
      })),
    ),
    {type: 'header', key: 'stations-header', source: 'stations'},
    ...sectionRows('stations', stations, stationRows(shown)),
    ...more,
  ];
};

/**
 * What a failed section says, in its row and in the spoken summary: too
 * many requests when it was refused for them (FE #427), else that it failed.
 */
export const searchFailedMessage = (
  source: SearchSourceName,
  throttled?: boolean,
): MessageDescriptor =>
  throttled
    ? messages.network.tooManyRequests
    : source === 'places'
      ? messages.stations.placeSearchFailed
      : messages.stations.stationSearchFailed;

const settled = (status: SearchStatus) =>
  status === 'ready' || status === 'error';

/**
 * The status message of a typed search (WCAG 4.1.3): what was found, once
 * both sources answered, or which failed, once both settled (offline, the
 * likely case, would otherwise be met with silence). Empty before that, and
 * around the user, where each app moves focus to the list instead.
 */
export const searchSummary = (
  {
    query,
    places,
    stations,
  }: {query: string | null; places: PlaceResults; stations: StationResults},
  formatMessage: IntlShape['formatMessage'],
): string => {
  if (query === null) {
    return '';
  }
  if (places.status === 'ready' && stations.status === 'ready') {
    return formatMessage(messages.stations.searchSummary, {
      places: places.items.length,
      stations: stations.count,
    });
  }
  if (!settled(places.status) || !settled(stations.status)) {
    return '';
  }
  const failures = [
    places.status === 'error'
      ? formatMessage(searchFailedMessage('places', places.throttled))
      : null,
    stations.status === 'error'
      ? formatMessage(searchFailedMessage('stations', stations.throttled))
      : null,
  ].filter((failure): failure is string => failure !== null);
  // Both refused for too many requests: said once.
  return [...new Set(failures)].join('\n');
};
